import { Users } from '@platform/client';
import { GetCurrentUser200, OrgRepUser } from '@platform/client/schemas';
import NextBestActions from '../next-best-actions';
import BusinessChallengesSection from '../business-challenges';
import { RejoinPanel } from './RejoinPanel';
import {
  isCorporateProNoCohort,
  isInactiveUser,
  isNonCorporatePro,
} from '../../util/ProgramHelpers';
import { IndividualInactivePanel } from './IndividualInactiveUserPanel';
import { userApplicationApproved } from '../../app';
import CohortPanel from '../cohort-panel';

/** The main content panel of the dashboard home page */
const DashboardHomePanel = () => {
  const { isSuccess, data: user } = Users.useGetCurrentUser({
    query: {
      // Throw an error if we fail to fetch user data after retries are complete
      // TODO: Find a more graceful way to error the frontend when we are unable to fetch core app data like this, outside of the Dashboard feature
      throwOnError: true,
    },
  });
  const isNonCorpPro = isNonCorporatePro(user);
  const isCorpProNoCohort = isCorporateProNoCohort(user);
  const isInactivePro = isInactiveUser(user) && user?.accountType === 'experteer';
  const hasDashboardAccess =
    userApplicationApproved(user) && !isNonCorpPro && !isInactivePro && !isCorpProNoCohort;

  return (
    <div className='px-4 sm:pl-11 w-full md:w-[920px] min-w-0 flex flex-col gap-4'>
      <div className='flex flex-col gap-4'>
        <div className='text-3xl'>Welcome back{isSuccess && `, ${user?.firstName}`}</div>
        {hasDashboardAccess && user?.accountType === 'organization' && (
          <OrganizationDashboardHomePanel user={user} />
        )}
        {hasDashboardAccess && user?.accountType === 'experteer' && (
          <ProfessionalDashboardHomePanel user={user} />
        )}
        {isCorpProNoCohort && <RejoinPanel user={user} />}
        {isNonCorpPro && <IndividualInactivePanel />}
      </div>
    </div>
  );
};

const OrganizationDashboardHomePanel = ({ user }: { user: OrgRepUser }) => {
  if (!user?.organization?.id) {
    // add a skeleton here?
    return null;
  }
  return <BusinessChallengesSection organizationId={user?.organization?.id} />;
};

const ProfessionalDashboardHomePanel = ({ user }: { user: GetCurrentUser200 }) => {
  return (
    <div className='flex flex-col gap-4'>
      <NextBestActions user={user} />
      {user?.cohort && <CohortPanel />}
    </div>
  );
};

export default DashboardHomePanel;
